import { createFileRoute, redirect } from "@tanstack/react-router";

import { createHead } from "@/components/createHead";
import { MarketplaceBody } from "@/components/MarketplacePage";
import { UserPage } from "@/components/UserPage";
import { getMonetizedFeeds, SearchResult } from "@/lib/api";
import { User } from "@/lib/types";

export const Route = createFileRoute("/_marketplace-layout/ads/user/$userId")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      feeds: search.feeds || "[]",
    } as {
      feeds?: string;
    };
  },
  loader: async ({ params: { userId }, location }) => {
    const visibleIds = JSON.parse(
      (location.search as { feeds: string }).feeds || "[]",
    ) as string[];

    if (!userId) {
      throw redirect({ to: "/ads" });
    }

    const { results } = await getMonetizedFeeds({
      user_id: userId,
      days: 30,
      page_size: 20,
    });

    const account_bio = results[0]?.account_bio;
    const adsUser = results[0]?.user;

    if (!adsUser) {
      throw new Error("User not found");
    }

    return {
      results: results as Omit<SearchResult, "algorithm">[],
      user: {
        ...adsUser,
        id: parseInt(userId),
      },
      account_bio,
      visibleIds,
    };
  },
  head: ({ loaderData }) => {
    const user = loaderData?.user as User;

    return createHead({
      title: `${user.display_name} Bluesky Ad Profile | Graze`,
      ogTitle: `${user.display_name} Bluesky Ad Profile`,
      description: `Browse all of the feeds from ${user.display_name} that you can run advertisements on.`,
      pathname: `/ads/user/${user.id}`,
      ogImage: `/ads/user/$userId/og.png`,
      ogParams: { userId: user.id },
    });
  },
});

function RouteComponent() {
  const loaderData = Route.useLoaderData();

  return (
    <MarketplaceBody>
      <UserPage
        adFeeds={loaderData.results as SearchResult[]}
        visibleIds={loaderData.visibleIds}
        user={loaderData.user}
        accountBio={loaderData.account_bio}
        adAction="run-on-all"
      />
    </MarketplaceBody>
  );
}
